import { Header, HTMLRender } from '_/components';
import ContainerWidthLimit from '_/components/ContainerWidthLimit';
import { colors } from '_/config/theme';
import { useCustomization } from '_/hooks/CustomizationContext';
import { useVisit } from '_/hooks/VisitContext';

import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { textReplace } from '_/helpers/textReplace';
import { IpadLang, Lang } from '@w3lcome/types';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { useCentralizedValuesFallback } from '_/hooks/useCentralizedValuesFallback';
import axios from 'axios';
import { AppRoute, CheckinProps, RootStackParamList } from '_/navigation/types';
import { useAuth } from '_/hooks/AuthContext';
import { ipadLogsApi } from '_/services/api';

type RouteProps = RouteProp<RootStackParamList, AppRoute.EXTRA_PRINT_SCREENS>;

export const ExtraPrintScreens: React.FC = () => {
  const { ipad } = useAuth();
  const { customization, company } = useCustomization();
  const { visit, navigateToHome } = useVisit();
  const { i18n } = useTranslation();
  const navigation = useNavigation<CheckinProps>();

  const route = useRoute<RouteProps>();
  const createdVisit = route.params?.createdVisit;

  const [step, setStep] = useState<'waiting' | 'success'>('waiting');

  const [cardNumber, setCardNumber] = useState(null);

  const { selectedOrFallbackVisitFlowType } = useCentralizedValuesFallback();

  const printingSuccessTimerMs = selectedOrFallbackVisitFlowType?.printSuccessTimeoutMs ?? 6000;
  const waitForPrinterTimerMs = selectedOrFallbackVisitFlowType?.printerWaitTimeoutMs ?? 30000;

  const waccessIntegration = company?.waccessIntegration;

  const getMessageByLang = (message: { [key in Lang]?: string } | undefined) => {
    if (!message) return '';

    if (typeof message === 'string') return message;

    const langValue =
      message[i18n.language as Lang] ||
      message[Lang.EN] ||
      message[Object.keys(message)[0] as Lang];

    return (langValue || '').trim();
  };

  const ipadPrintSuccessMessage = useMemo(() => {
    const message = getMessageByLang(selectedOrFallbackVisitFlowType?.ipadPrintSuccessMessage);
    return textReplace(message, i18n.language as IpadLang, customization, visit);
  }, [
    customization,
    visit,
    i18n.language,
    selectedOrFallbackVisitFlowType?.ipadPrintSuccessMessage,
  ]);

  const ipadWaitingForPrinterMessage = useMemo(() => {
    const message = getMessageByLang(selectedOrFallbackVisitFlowType?.ipadWaitingForPrinterMessage);
    return textReplace(message, i18n.language as IpadLang, customization, visit);
  }, [
    customization,
    visit,
    i18n.language,
    selectedOrFallbackVisitFlowType?.ipadWaitingForPrinterMessage,
  ]);

  useEffect(() => {
    if (!selectedOrFallbackVisitFlowType?.requirePrinterCardNumberConfirmation) {
      setStep('success');
      return;
    }

    let isCancelled = false;

    const requestCardNumber = async () => {
      try {
        const targetVisit = createdVisit || visit;

        const { data } = await axios.post(
          ipad?.waccessEventsEndpointUrl ?? '',
          {
            CHID: 0,
            metadata: targetVisit.metadata,
            treated: false,
            visitId: targetVisit?.id ?? '',
            meetingId: targetVisit?.meetingId ?? '',
            returnMessage: '',
            pictureUrl: targetVisit?.pictureUrl ?? '',
          },
          {
            headers: {
              WAccessAuthentication: `${waccessIntegration?.username}:${waccessIntegration?.password}`,
              WAccessUtcOffset: '-180',
              'Access-Control-Allow-Origin': '*',
            },
            timeout: waitForPrinterTimerMs,
          }
        );

        if (isCancelled) return;

        const { treated, CardNumber } = data ?? {};

        if (!treated || !CardNumber || CardNumber === 0) {
          ipadLogsApi.insert({
            companyId: ipad?.companyId,
            ipadId: ipad?.id,
            type: 'Printer Poll Error',
            message: `Resposta inválida - treated: ${treated} - CardNumber: ${CardNumber}`,
          });
          navigation.replace(AppRoute.CHECKIN_ERROR_SCREEN);
          return;
        }

        setCardNumber(CardNumber);
        setStep('success');
      } catch (error) {
        if (isCancelled) return;

        ipadLogsApi.insert({
          companyId: ipad?.companyId,
          ipadId: ipad?.id,
          type: 'Printer Poll Error',
          message: `Erro/timeout aguardando resposta do post: ${
            error?.response?.data?.message || error.message || 'Erro desconhecido'
          } - VisitId: ${visit?.id}`,
        });

        navigation.replace(AppRoute.CHECKIN_ERROR_SCREEN);
      }
    };

    requestCardNumber();

    return () => {
      isCancelled = true;
    };
  }, [selectedOrFallbackVisitFlowType?.requirePrinterCardNumberConfirmation]);

  useEffect(() => {
    if (step !== 'success') return;

    const timer = setTimeout(async () => {
      if (cardNumber && visit.id) {
        try {
          // await visitsApi.update(visit.id, { cardNumber });
          navigateToHome(navigation);
        } catch (error) {
          ipadLogsApi.insert({
            companyId: ipad?.companyId,
            ipadId: ipad?.id,
            type: 'Update Card Number Error',
            message: `VisitId: ${visit.id} - Card Number: ${cardNumber}
        STATUS: ${error?.response?.status ?? 'NO_RESPONSE'}
        ERROR: ${error?.response?.data?.message || error.message || 'Unknown error'}`,
          });
          navigation.replace(AppRoute.CHECKIN_ERROR_SCREEN);
        }
      } else {
        navigateToHome(navigation);
      }
    }, printingSuccessTimerMs);

    return () => clearTimeout(timer);
  }, [step, cardNumber]);

  const currentMessage =
    step === 'waiting' ? ipadWaitingForPrinterMessage : ipadPrintSuccessMessage;

  return (
    <View style={styles.container}>
      <ContainerWidthLimit>
        <Header hideReturnButton />
        <View style={styles.content}>
          <HTMLRender html={currentMessage} />
        </View>
      </ContainerWidthLimit>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
    alignItems: 'center',
  },
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 64,
    textAlign: 'center',
  },
});
